import { Fragment } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import LegalLinks, { LEGAL_PAGES } from "@/components/LegalLinks";
import { COMPANY } from "./company";
import { useLegal } from "./content";
import LanguageSwitcher from "@/components/LanguageSwitcher";

// Turns e-mail addresses, https URLs and internal legal paths inside plain strings into links.
const TOKEN = /([\w.+-]+@[\w-]+\.[\w.]+|https?:\/\/[^\s),]+|\/(?:privacy|termsofuse|support)\b)/g;
export function Rich({ text }) {
  const parts = String(text).split(TOKEN);
  return parts.map((part, i) => {
    if (i % 2 === 0) return <Fragment key={i}>{part}</Fragment>;
    const cls = "text-[#f4f1eb] underline underline-offset-[3px] decoration-[#B59560]/60 hover:decoration-[#B59560]";
    if (part.includes("@")) return <a key={i} href={`mailto:${part}`} className={cls}>{part}</a>;
    if (part.startsWith("/")) return <Link key={i} to={part} className={cls}>{part.slice(1)}</Link>;
    return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className={cls}>{part.replace(/^https?:\/\//, "")}</a>;
  });
}

function Block({ b }) {
  if (typeof b === "string") return <p className="mb-4"><Rich text={b} /></p>;
  if (b.list) {
    return (
      <ul className="mb-5 space-y-2">
        {b.list.map((li, i) => (
          <li key={i} className="relative pl-5">
            <span aria-hidden="true" className="absolute left-0 top-[0.72em] h-px w-2.5 bg-[#B59560]" />
            <Rich text={li} />
          </li>
        ))}
      </ul>
    );
  }
  if (b.kv) {
    return (
      <dl className="mb-5 grid grid-cols-1 sm:grid-cols-[minmax(0,13rem)_1fr] border-t border-white/10">
        {b.kv.map(([k, v]) => (
          <Fragment key={k}>
            <dt className="font-mono-data text-[0.62rem] tracking-[0.16em] uppercase text-white/45 pt-3 sm:py-3 sm:border-b border-white/10">{k}</dt>
            <dd className="pb-3 pt-1 sm:py-3 border-b border-white/10 text-[#f4f1eb]"><Rich text={v} /></dd>
          </Fragment>
        ))}
      </dl>
    );
  }
  if (b.note) {
    return (
      <p className="mb-5 border-l-2 border-[#B59560] bg-white/[0.03] px-4 py-3 text-[#f4f1eb]/90">
        <Rich text={b.note} />
      </p>
    );
  }
  return null;
}

export function Sections({ sections }) {
  return sections.map((s, i) => (
    <section key={s.id} id={s.id} className="scroll-mt-24 border-t border-white/10 pt-8 pb-4 first:border-t-0 first:pt-0">
      <h2 className="font-gateway font-bold text-[1.25rem] md:text-[1.45rem] tracking-tight text-[#f4f1eb] mb-4 flex gap-3">
        <span className="font-mono-data text-[0.7rem] tracking-[0.18em] text-[#B59560] pt-[0.55rem]">{String(i + 1).padStart(2, "0")}</span>
        <span>{s.title}</span>
      </h2>
      {s.body.map((b, j) => <Block key={j} b={b} />)}
    </section>
  ));
}

// Company details block — closes each of the three pages (Albanian law 10128/2009 art. 7, law 9901/2008 art. 11)
function CompanyDetails({ ui }) {
  const rows = [
    [ui.cd.company, COMPANY.legalName],
    ...(COMPANY.nuis ? [[ui.cd.nipt, COMPANY.nuis]] : []),
    [ui.cd.office, COMPANY.address],
    [ui.cd.email, COMPANY.supportEmail],
    [ui.cd.phone, COMPANY.supportPhone],
  ];
  return (
    <section id="company-details" className="mt-14 border border-white/10 bg-white/[0.03]" data-testid="company-details">
      <div className="px-6 pt-6 pb-2 flex items-center gap-3">
        <span className="h-px w-6 bg-[#B59560]" aria-hidden="true" />
        <p className="font-mono-data text-[0.62rem] tracking-[0.22em] text-[#B59560]">{ui.companyDetails}</p>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-[minmax(0,15rem)_1fr] px-6 pb-4 [&>dd:last-child]:border-0 sm:[&>dt:nth-last-child(2)]:border-0">
        {rows.map(([k, v]) => (
          <Fragment key={k}>
            <dt className="font-mono-data text-[0.6rem] tracking-[0.16em] uppercase text-white/45 pt-3 sm:py-3 sm:border-b border-white/10">{k}</dt>
            <dd className="pb-3 pt-1 sm:py-3 border-b border-white/10 text-[#f4f1eb]"><Rich text={v} /></dd>
          </Fragment>
        ))}
      </dl>
    </section>
  );
}

export default function LegalLayout({ title, kicker, intro, toc = [], children, testid }) {
  const { t } = useLang();
  const { ui } = useLegal();
  const navigate = useNavigate();
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/"));

  return (
    <main className="grain bg-[#0a0a0a] text-[#f4f1eb]/80 min-h-screen relative font-body" data-testid={testid}>
      <header className="sticky top-0 z-40 bg-[#0a0a0a]/90 backdrop-blur border-b border-white/10">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-3 min-w-0">
            <span className="font-gateway font-bold tracking-[0.24em] text-[0.8rem] text-[#f4f1eb]">EAGLE INC</span>
            <span className="hidden sm:inline font-mono-data text-[0.58rem] tracking-[0.2em] text-white/40 truncate">{ui.header}</span>
          </div>
          <div className="flex items-center gap-4">
          <LanguageSwitcher variant="dark" />
          <button
            type="button"
            onClick={goBack}
            data-testid="legal-back"
            className="inline-flex items-center gap-2 font-mono-data text-[0.62rem] tracking-[0.2em] text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {ui.back}
          </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1180px] px-6 md:px-8 pt-12 md:pt-16 pb-16 grid grid-cols-1 lg:grid-cols-[15rem_1fr] gap-10 lg:gap-16">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <nav aria-label={ui.nav} className="flex lg:flex-col gap-2 lg:gap-0 flex-wrap">
            {LEGAL_PAGES.map((p) => (
              <NavLink
                key={p.to}
                to={p.to}
                className={({ isActive }) =>
                  `font-mono-data text-[0.64rem] tracking-[0.2em] uppercase px-3 py-2 lg:px-0 lg:py-2.5 lg:border-b border lg:border-x-0 lg:border-t-0 transition-colors ${
                    isActive ? "text-[#f4f1eb] border-[#B59560]" : "text-white/45 border-white/10 hover:text-white/80"
                  }`
                }
              >
                {t.common[p.key]}
              </NavLink>
            ))}
          </nav>
          {toc.length > 0 && (
            <div className="hidden lg:block mt-10">
              <p className="font-mono-data text-[0.56rem] tracking-[0.22em] text-white/35 mb-3">{ui.onThisPage}</p>
              <ol className="space-y-1.5">
                {toc.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="text-[0.78rem] text-white/50 hover:text-white transition-colors leading-snug block">{s.title}</a>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </aside>

        <article className="min-w-0 max-w-[46rem] text-[0.95rem] leading-[1.75]">
          <p className="font-mono-data text-[0.62rem] tracking-[0.22em] text-[#B59560] mb-4">{kicker}</p>
          <h1 className="font-gateway font-bold text-[2.3rem] md:text-[3.2rem] leading-[1.02] tracking-tight text-[#f4f1eb] mb-5">{title}</h1>
          {intro && <p className="text-[1.02rem] text-[#f4f1eb]/75 mb-3 max-w-[40rem]"><Rich text={intro} /></p>}
          <p className="font-mono-data text-[0.6rem] tracking-[0.18em] text-white/40 mb-12">
            {COMPANY.legalName.toUpperCase()} · {ui.lastUpdated} {ui.effective.toUpperCase()}
          </p>
          {children}
          <CompanyDetails ui={ui} />
        </article>
      </div>

      <footer className="border-t border-white/10" data-testid="legal-footer">
        <div className="mx-auto max-w-[1180px] px-6 md:px-8 py-8 flex flex-wrap items-end justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] gap-6">
          <div>
            <p className="font-gateway font-bold tracking-[0.24em] text-[0.72rem] text-[#f4f1eb]">{COMPANY.legalName.toUpperCase()}</p>
            <p className="font-mono-data text-[0.58rem] tracking-[0.18em] text-white/40 mt-1.5">{t.common.company}</p>
          </div>
          <LegalLinks className="font-mono-data text-[0.58rem] tracking-[0.18em] text-white/40 order-last w-full text-center lg:order-none lg:w-auto" />
          <span className="lg:justify-self-end font-mono-data text-[0.6rem] tracking-[0.22em] text-white/40">© {new Date().getFullYear()} Eagle Inc</span>
        </div>
      </footer>
    </main>
  );
}
